import { kybStyles } from "../kybStyles";
import { KybLinkRecord } from "@services/models/Kyb/GetKybLinks";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

export type KybRecordAction = "status" | "unmap" | "delete";

interface KybRowActionsProps {
  data: KybLinkRecord;
  jwt: string | null;
  onAction: (action: KybRecordAction, record: KybLinkRecord) => void;
}

const KybRowActions: React.FC<KybRowActionsProps> = ({
  data,
  jwt,
  onAction,
}) => {
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const { handleSuccess, handleError } = useSnackbarMessage();
  const name = data.kyb_data?.merchantName || "unnamed merchant";
  const copyLink = `https://kyb.tylt.money/${data.id}`;
  const openLink = `${copyLink}${jwt ? `?token=${encodeURIComponent(jwt)}` : ""}`;

  const closeMenu = (restoreFocus = false) => {
    setMenuPosition(null);
    if (restoreFocus) triggerRef.current?.focus();
  };

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  useEffect(() => {
    if (!menuPosition) return;
    menuRef.current?.querySelector<HTMLElement>("[role=menuitem]")?.focus();
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !menuRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      )
        setMenuPosition(null);
    };
    const onScroll = (event: Event) => {
      if (
        !(event.target instanceof Node) ||
        !menuRef.current?.contains(event.target)
      )
        setMenuPosition(null);
    };
    const onResize = () => setMenuPosition(null);
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [menuPosition]);

  const openMenu = () => {
    if (menuPosition) {
      closeMenu();
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const menuHeight = data.user_id ? 190 : 150;
    setMenuPosition({
      left: Math.max(8, Math.min(rect.right - 216, window.innerWidth - 224)),
      top:
        rect.bottom + menuHeight > window.innerHeight
          ? Math.max(8, rect.top - menuHeight - 6)
          : rect.bottom + 6,
    });
  };

  const chooseAction = (action: KybRecordAction) => {
    closeMenu(true);
    onAction(action, data);
  };

  return (
    <div className="flex w-full items-center justify-start gap-1">
      <button
        type="button"
        className={`${kybStyles.focus} inline-flex items-center justify-start gap-[6px] min-w-[73px] py-[6px] pr-[7px] pl-0 border-0 rounded-[5px] bg-[transparent] text-[10px] cursor-pointer [font-family:inherit] [transition:color_120ms_ease] [&_.material-symbols-outlined]:text-[15px] ${copied ? "text-text-status-green" : "text-text-subtle hover:text-text-navbar-active"}`}
        aria-label={`Copy KYB link for ${name}`}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(copyLink);
            setCopied(true);
            handleSuccess({ message: "KYB link copied." });
            clearTimeout(copyTimer.current);
            copyTimer.current = setTimeout(() => setCopied(false), 2000);
          } catch {
            handleError({
              message: "Could not copy the link. Please try again.",
            });
          }
        }}
      >
        <span className="material-symbols-outlined" aria-hidden="true">
          {copied ? "check" : "content_copy"}
        </span>
        <span aria-live="polite">{copied ? "Copied" : "Copy"}</span>
      </button>
      <button
        ref={triggerRef}
        type="button"
        className={
          menuPosition ? kybStyles.activeIconButton : kybStyles.iconButton
        }
        aria-label={`Actions for ${name}`}
        title="Record settings"
        aria-haspopup="menu"
        aria-expanded={Boolean(menuPosition)}
        onClick={openMenu}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            if (!menuPosition) openMenu();
          }
        }}
      >
        <span className="material-symbols-outlined" aria-hidden="true">
          settings
        </span>
      </button>
      {menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            className={`light fixed z-[1200] flex flex-col w-[216px] p-[5px] border border-border-subtle rounded-[9px] text-text-base bg-bg-page-body shadow-[0_6px_24px_rgb(21_24_44_/_10%)] [font-family:Inter,sans-serif] ${kybStyles.motion}`}
            role="menu"
            aria-label={`Actions for ${name}`}
            style={menuPosition}
            onBlur={(event) => {
              const nextTarget = event.relatedTarget as Node | null;
              // Let the trigger's click toggle the open menu after focus moves.
              if (
                !event.currentTarget.contains(nextTarget) &&
                !triggerRef.current?.contains(nextTarget)
              )
                closeMenu();
            }}
            onKeyDown={(event) => {
              const items = Array.from(
                event.currentTarget.querySelectorAll<HTMLElement>(
                  "[role=menuitem]",
                ),
              );
              const index = items.indexOf(
                document.activeElement as HTMLElement,
              );
              if (event.key === "Tab") closeMenu(true);
              if (event.key === "Escape") {
                event.preventDefault();
                closeMenu(true);
              }
              if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
                event.preventDefault();
                const next =
                  event.key === "Home"
                    ? 0
                    : event.key === "End"
                      ? items.length - 1
                      : (index +
                          (event.key === "ArrowDown" ? 1 : -1) +
                          items.length) %
                        items.length;
                items[next]?.focus();
              }
            }}
          >
            <a
              role="menuitem"
              className={kybStyles.menuItem}
              tabIndex={-1}
              href={openLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => closeMenu(true)}
            >
              <span className={kybStyles.menuIcon} aria-hidden="true">
                open_in_new
              </span>
              Open KYB link
            </a>
            <button
              type="button"
              role="menuitem"
              className={kybStyles.menuItem}
              tabIndex={-1}
              onClick={() => chooseAction("status")}
            >
              <span className={kybStyles.menuIcon} aria-hidden="true">
                swap_horiz
              </span>
              Mark as {data.is_submitted ? "pending" : "submitted"}
            </button>
            {data.user_id && (
              <button
                type="button"
                role="menuitem"
                className={kybStyles.menuItem}
                tabIndex={-1}
                onClick={() => chooseAction("unmap")}
              >
                <span className={kybStyles.menuIcon} aria-hidden="true">
                  link_off
                </span>
                Unassign user
              </button>
            )}
            <div
              className="h-px mx-[5px] my-1 bg-border-subtle"
              role="separator"
            />
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={kybStyles.dangerMenuItem}
              onClick={() => chooseAction("delete")}
            >
              <span
                className="material-symbols-outlined text-[17px] text-text-status-red"
                aria-hidden="true"
              >
                delete
              </span>
              Delete KYB record
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default KybRowActions;
